import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';

import { PaperButtonComponent } from '@ricoh-paper/angular';

import { ANALYTICS_EVENTS } from '../../../core/analytics/events';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { TaskCapabilityService } from '../../../core/services/task-capability.service';
import { type TaskDetailSurface } from '../../../core/services/task-detail.service';
import { PaperConfirmDialogService } from '../../../shared/services/paper-confirm-dialog.service';
import { FocusTrapDirective } from '../../../shared/directives/focus-trap.directive';
import { TaskService } from '../../../features/schedule/services/task.service';
import {
  capabilityTaskSourceFor,
  type ScheduleTask,
} from '../../../features/schedule/models/schedule-types';
import { localDateStr } from '../../../core/utils/text';
import { withClock } from '../../../core/utils/format-prefs';
import { safeTaskSourceUrl } from '../../../core/utils/task-source-url';
import { SpIconComponent } from '../components/shared/sp-icon.component';
import { dayLabelOf } from '../data/personal-adapt';

/**
 * sp-task-detail — right-slide TASK view, in the schedule's chrome and
 * tokens, so a task reads the same from every page.
 *
 * READ-FIRST, ACT-FAST:
 *   - Header: title, status pill, priority chip, when-line.
 *   - Fields: when / duration / due / type / source; description (the
 *     dialog's rich text); AI context when the task was AI-created; tags.
 *   - Quick actions (capability-gated per source — Planner/To Do rows can
 *     be read-only): Mark done, Defer to tomorrow, Edit (opens the full
 *     task editor), Delete, and Open in <source> for external tasks.
 *
 * MUTATIONS run through the root TaskService, whose `changedTick` makes
 * every task surface (home My Day / My Week, the redesign calendar,
 * widgets) re-query — an action here updates the page underneath live.
 */
@Component({
  selector: 'sp-task-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperButtonComponent, SpIconComponent, FocusTrapDirective],
  template: `
    <div class="tv-scrim" (click)="close.emit()"></div>
    <div
      class="tv-panel ph-mask"
      role="dialog"
      aria-label="Task details"
      [attr.data-priority]="task().priority"
      [attr.aria-modal]="trapActive() ? 'true' : null"
      [attr.aria-hidden]="trapActive() ? null : 'true'"
      [attr.inert]="trapActive() ? null : ''"
      [imFocusTrap]="trapActive()"
    >
      <div class="tv-head">
        <div class="tv-ledger">
          <span>Task</span>
          <i></i>
          <span>{{ sourceLongLabel() }}</span>
        </div>
        <div class="tv-head-actions">
          @if (canEdit()) {
            <paper-button intent="ghost" class="tv-edit ph-no-capture" data-testid="task-edit" (click)="onEdit()">
              <sp-icon name="wrench" [size]="14" /> Edit
            </paper-button>
          }
          <paper-button intent="ghost" class="tv-ic" title="Close" ariaLabel="Close" (click)="close.emit()"><sp-icon name="chevR" [size]="18" /></paper-button>
        </div>
        <div class="tv-title-row">
          <h2 [class.done]="isDone()">{{ task().title }}</h2>
          <span class="status-pill" [attr.data-s]="task().status">{{ statusLabel() }}</span>
        </div>
        <div class="tv-sub">
          <span class="tv-prio" [attr.data-p]="task().priority">{{ priorityLabel() }}</span>
          {{ subLine() }}
        </div>
        <div class="tv-kind"><sp-icon name="check" [size]="12" /> Task{{ sourceLabel() ? ' · ' + sourceLabel() : '' }}</div>
      </div>

      <div class="tv-scroll">
        <div class="tv-grid">
          <div class="tv-field"><span class="tv-fl">When</span><span class="tv-fv">{{ whenLabel() }}</span></div>
          <div class="tv-field"><span class="tv-fl">Duration</span><span class="tv-fv">{{ durationLabel() }}</span></div>
          @if (task().dueDate) {
            <div class="tv-field"><span class="tv-fl">Due</span><span class="tv-fv">{{ dueLabel() }}</span></div>
          }
          <div class="tv-field"><span class="tv-fl">Type</span><span class="tv-fv">{{ typeLabel() }}</span></div>
          <div class="tv-field"><span class="tv-fl">Source</span><span class="tv-fv">{{ sourceLongLabel() }}</span></div>
          @if (task().completedAt) {
            <div class="tv-field"><span class="tv-fl">Completed</span><span class="tv-fv">{{ completedLabel() }}</span></div>
          }
        </div>

        @if (task().description) {
          <div class="tv-block">
            <div class="tv-block-h">Description</div>
            <!-- Rich text from the task editor (Tiptap HTML — Angular sanitises). -->
            <div class="tv-desc" [innerHTML]="task().description"></div>
          </div>
        }

        @if (task().aiGenerated || task().aiSuggestion) {
          <div class="tv-block">
            <div class="tv-block-h">AI planning context</div>
            <div class="tv-ai">
              <sp-icon name="sparkle" [size]="13" />
              <span>{{ task().aiSuggestion || (task().aiScheduled ? 'AI picked this time slot for you.' : 'This task was drafted by AI.') }}</span>
            </div>
          </div>
        }

        @if (task().tags.length) {
          <div class="tv-block">
            <div class="tv-block-h">Tags</div>
            <div class="tv-tags">
              @for (tag of task().tags; track tag) {
                <span class="tv-tag">{{ tag }}</span>
              }
            </div>
          </div>
        }

        <!-- Quick actions — capability-gated; external sources that can't
             be written inline get the deep link instead. -->
        <div class="tv-block">
          <div class="tv-block-h">Quick actions</div>
          <div class="tv-actions">
            @if (!isDone() && canComplete()) {
              <paper-button intent="ghost" class="tv-act ph-no-capture" data-testid="task-complete" [disabled]="busy()" (click)="onComplete()">
                <sp-icon name="checkCircle" [size]="13" /> Mark done
              </paper-button>
            }
            @if (!isDone() && canEdit()) {
              <paper-button intent="ghost" class="tv-act ph-no-capture" data-testid="task-defer" [disabled]="busy()" (click)="onDefer()">
                <sp-icon name="clock" [size]="13" /> Defer to tomorrow
              </paper-button>
            }
            @if (deepLinkUrl(); as url) {
              <paper-button intent="ghost" class="tv-act ph-no-capture" (click)="onOpenSource(url)">
                <sp-icon name="link" [size]="13" /> Open in {{ sourceLabel() || 'source' }}
              </paper-button>
            }
            @if (canDelete()) {
              <paper-button intent="ghost" class="tv-act danger ph-no-capture" data-testid="task-delete" [disabled]="busy()" (click)="onDelete()">
                <sp-icon name="x" [size]="13" /> Delete task
              </paper-button>
            }
          </div>
          @if (restrictionNote(); as note) {
            <div class="tv-restricted"><sp-icon name="shield" [size]="12" /> {{ note }}</div>
          }
        </div>
      </div>

      @if (toolToast(); as msg) {
        <div class="tv-toast" role="status">{{ msg }}</div>
      }
    </div>
  `,
  styles: `
    :host { position: absolute; inset: 0; z-index: 50; }
    .tv-scrim { position: absolute; inset: 0; background: rgba(16, 20, 30, 0.32); backdrop-filter: blur(2px); animation: tv-fade 0.2s var(--sp-ease); }
    .tv-panel { --tv-ink: var(--sp-accent); position: absolute; top: 0; right: 0; bottom: 0; width: min(430px, 96vw); background: var(--sp-surface); border-left: 1px solid var(--sp-border); box-shadow: var(--sp-shadow-lg); display: flex; flex-direction: column; animation: tv-slide 0.26s var(--sp-spring); }
    .tv-panel[data-priority='1'] { --tv-ink: var(--task-p1, var(--sp-danger)); }
    .tv-panel[data-priority='2'] { --tv-ink: var(--task-p2, var(--sp-warning)); }
    .tv-panel[data-priority='3'] { --tv-ink: var(--task-p3, var(--sp-accent)); }
    .tv-panel[data-priority='4'] { --tv-ink: var(--task-p4, var(--sp-text-faint)); }
    .tv-head { flex: none; padding: 15px 22px 14px; border-bottom: 1px solid var(--sp-divider); background: var(--sp-surface-2); }
    .tv-ledger { display: flex; align-items: center; gap: 9px; min-height: 18px; padding-right: 118px; font-size: calc(10px + var(--sp-fs-bump)); font-weight: 720; letter-spacing: 0.07em; text-transform: uppercase; color: var(--sp-text-faint); }
    .tv-ledger span:first-child { color: var(--tv-ink); }
    .tv-ledger i { width: 26px; height: 1px; background: var(--sp-border-strong); }
    .tv-head-actions { display: flex; justify-content: flex-end; gap: 6px; margin-top: -18px; margin-bottom: 3px; }
    paper-button.tv-ic, paper-button.tv-edit, paper-button.tv-act { border: none; background: transparent; cursor: pointer; }
    paper-button.tv-edit, paper-button.tv-act { --im-button-label-gap: var(--s-2, 8px); }
    paper-button.tv-ic { display: grid; place-items: center; width: 30px; height: 30px; border-radius: var(--sp-radius-sm); color: var(--sp-text-faint); }
    paper-button.tv-ic:hover { background: var(--sp-surface-hover); color: var(--sp-text); }
    paper-button.tv-edit { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border: 1px solid var(--sp-border); border-radius: var(--sp-radius-sm); font-size: calc(12px + var(--sp-fs-bump)); font-weight: 650; color: var(--sp-text); }
    paper-button.tv-edit:hover { border-color: var(--sp-accent); background: var(--sp-accent-soft); }
    .tv-title-row { display: flex; align-items: center; gap: 10px; min-width: 0; }
    .tv-title-row h2 { margin: 0; font-size: 17.5px; font-weight: 750; letter-spacing: -0.015em; color: var(--sp-text); overflow: hidden; text-overflow: ellipsis; }
    .tv-title-row h2.done { text-decoration: line-through; color: var(--sp-text-muted); }
    .status-pill { flex: none; padding: 2px 9px; border-radius: var(--sp-radius-full); font-size: calc(10.5px + var(--sp-fs-bump)); font-weight: 700; background: var(--sp-surface-sunken); color: var(--sp-text-muted); }
    .status-pill[data-s='done'] { background: color-mix(in srgb, var(--sp-success) 14%, transparent); color: var(--sp-success); }
    .status-pill[data-s='in_progress'] { background: var(--sp-accent-soft); color: var(--sp-accent); }
    .status-pill[data-s='deferred'], .status-pill[data-s='cancelled'] { background: var(--sp-surface-sunken); color: var(--sp-text-faint); }
    .tv-sub { margin-top: 6px; display: flex; align-items: center; gap: 8px; font-size: calc(12.5px + var(--sp-fs-bump)); color: var(--sp-text-muted); }
    .tv-prio { padding: 1px 8px; border-radius: var(--sp-radius-full); border: 1px dashed var(--sp-border-strong); font-size: calc(10.5px + var(--sp-fs-bump)); font-weight: 700; color: var(--sp-text-muted); }
    .tv-prio[data-p='1'] { color: var(--sp-danger); border-color: color-mix(in srgb, var(--sp-danger) 55%, transparent); }
    .tv-prio[data-p='2'] { color: var(--sp-warning); border-color: color-mix(in srgb, var(--sp-warning) 55%, transparent); }
    .tv-kind { margin-top: 8px; display: inline-flex; align-items: center; gap: 6px; font-size: calc(11px + var(--sp-fs-bump)); font-weight: 650; color: var(--sp-text-faint); }
    .tv-scroll { flex: 1; min-height: 0; overflow-y: auto; padding: 16px 22px 24px; }
    .tv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; }
    .tv-field { display: flex; flex-direction: column; gap: 3px; }
    .tv-fl { font-size: calc(10px + var(--sp-fs-bump)); text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; color: var(--sp-text-faint); }
    .tv-fv { font-size: calc(13px + var(--sp-fs-bump)); font-weight: 600; color: var(--sp-text); }
    .tv-block { margin-top: 20px; }
    .tv-block-h { font-size: calc(10.5px + var(--sp-fs-bump)); text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; color: var(--sp-text-faint); margin-bottom: 9px; }
    .tv-desc { font-size: calc(13px + var(--sp-fs-bump)); line-height: 1.55; color: var(--sp-text); overflow-wrap: anywhere; }
    .tv-desc :where(p) { margin: 0 0 8px; }
    .tv-desc :where(ul, ol) { margin: 0 0 8px; padding-left: 18px; }
    .tv-ai { display: flex; gap: 8px; align-items: flex-start; padding: 10px 12px; border: 1px dashed var(--sp-border-strong); border-radius: var(--sp-radius-md, 12px); font-size: calc(12.5px + var(--sp-fs-bump)); line-height: 1.5; color: var(--sp-text-muted); }
    .tv-ai sp-icon { color: var(--sp-accent); flex: none; margin-top: 1px; }
    .tv-tags { display: flex; flex-wrap: wrap; gap: 6px; }
    .tv-tag { padding: 2px 9px; border-radius: var(--sp-radius-full); background: var(--sp-surface-sunken); font-size: calc(11px + var(--sp-fs-bump)); font-weight: 650; color: var(--sp-text-muted); }
    .tv-actions { display: flex; flex-wrap: wrap; gap: 8px; }
    paper-button.tv-act { display: inline-flex; align-items: center; gap: 7px; font-size: calc(12.5px + var(--sp-fs-bump)); font-weight: 600; color: var(--sp-accent); border: 1px dashed var(--sp-border); border-radius: var(--sp-radius-sm); padding: 7px 12px; background: var(--sp-surface); transition: all 0.15s var(--sp-ease); }
    paper-button.tv-act:hover { border-color: var(--sp-accent); background: var(--sp-accent-soft); }
    paper-button.tv-act.danger { color: var(--sp-danger); }
    paper-button.tv-act.danger:hover { border-color: var(--sp-danger); background: color-mix(in srgb, var(--sp-danger) 8%, transparent); }
    .tv-restricted { margin-top: 10px; display: flex; align-items: center; gap: 6px; font-size: calc(11.5px + var(--sp-fs-bump)); color: var(--sp-text-faint); }
    .tv-toast { position: absolute; bottom: 18px; left: 50%; transform: translateX(-50%); padding: 8px 16px; border-radius: var(--sp-radius-full); background: var(--sp-text); color: var(--sp-text-inverse, #fff); font-size: calc(12.5px + var(--sp-fs-bump)); font-weight: 600; box-shadow: var(--sp-shadow-md); animation: tv-fade 0.2s var(--sp-ease); }
    @keyframes tv-fade { from { opacity: 0; } }
    @keyframes tv-slide { from { transform: translateX(40px); opacity: 0; } }
    @media (max-width: 640px) { .tv-panel { width: 100vw; } }
    @media (prefers-reduced-motion: reduce) { .tv-scrim, .tv-panel, .tv-toast { animation: none; } }
  `,
})
export class SpTaskDetailComponent {
  readonly task = input.required<ScheduleTask>();
  /** Which page opened the panel (analytics attribution). */
  readonly surface = input<TaskDetailSurface>('other');
  /** Disabled while the full editor is layered above this read view. */
  readonly trapActive = input(true);

  readonly close = output<void>();
  /** Open the full task editor (the shared im-task-dialog). */
  readonly edit = output<ScheduleTask>();
  /** A mutation changed/removed the task (host may patch its copy). */
  readonly changed = output<ScheduleTask>();

  private readonly tasks = inject(TaskService);
  private readonly capabilities = inject(TaskCapabilityService);
  private readonly confirmDialog = inject(PaperConfirmDialogService);
  private readonly analytics = inject(AnalyticsService);

  protected readonly busy = signal(false);
  protected readonly toolToast = signal<string | null>(null);
  private _toastTimer: ReturnType<typeof setTimeout> | null = null;

  private _openedForId: string | null = null;

  constructor(){
    // Capability snapshot is needed for the action gating (idempotent).
    void this.capabilities.load();
    effect(() => {
      const t = this.task();
      if (t._id === this._openedForId) return;
      this._openedForId = t._id;
      untracked(() =>
        this.analytics.capture(ANALYTICS_EVENTS.TASK_DETAIL_OPENED, {
          task_id: t._id,
          task_source: this.capabilitySource(),
          status: t.status,
          is_timeboxed: !!t.scheduledStartTime,
          surface: this.surface(),
        }),
      );
    });
  }

  protected readonly isDone = computed(() => this.task().status === 'done');

  /** 'rdx' for native tasks; 'planner' / 'todo' for aggregated externals. */
  protected readonly capabilitySource = computed(() => {
    const t = this.task();
    return capabilityTaskSourceFor(t.source, t.sourceRef);
  });

  /** Workspace-managed rows stay read-only regardless of capabilities. */
  private readonly sourceLocked = computed(() => {
    const src = this.task().source;
    return src === 'system' || src === 'admin';
  });

  protected readonly canComplete = computed(
    () => !this.sourceLocked() && this.capabilities.can(this.capabilitySource(), 'update-status'),
  );
  protected readonly canEdit = computed(
    () => !this.sourceLocked() && this.capabilities.can(this.capabilitySource(), 'update-fields'),
  );
  protected readonly canDelete = computed(
    () => !this.sourceLocked() && this.capabilities.can(this.capabilitySource(), 'delete'),
  );

  /** Deep link into Planner / To Do for externally-owned rows. */
  protected readonly deepLinkUrl = computed(() => {
    const t = this.task();
    const rawUrl =
      t.sourceUrl ??
      this.capabilities.restrictionFor(this.capabilitySource(), 'update-status')
        ?.deepLinkUrl;
    return safeTaskSourceUrl(rawUrl);
  });

  /** One explanation line when the inline verbs are restricted. */
  protected readonly restrictionNote = computed(() => {
    if (this.sourceLocked()) return 'Managed by your workspace — read-only here.';
    if (this.canComplete() || this.canEdit()) return null;
    const restriction = this.capabilities.restrictionFor(
      this.capabilitySource(),
      'update-status',
    );
    return restriction?.remediation ?? 'This task is read-only here — use its source app to change it.';
  });

  // ── Labels ──
  protected readonly statusLabel = computed(() => {
    const map: Record<string, string> = {
      backlog: 'Backlog',
      planned: 'Planned',
      in_progress: 'In progress',
      done: 'Done',
      deferred: 'Deferred',
      cancelled: 'Cancelled',
    };
    return map[this.task().status] ?? this.task().status;
  });
  protected readonly priorityLabel = computed(() => {
    const map: Record<number, string> = { 1: 'Critical', 2: 'High', 3: 'Medium', 4: 'Low' };
    return `${map[this.task().priority] ?? 'P' + this.task().priority} priority`;
  });
  protected readonly typeLabel = computed(() => {
    const map: Record<string, string> = {
      task: 'Task',
      habit: 'Habit',
      focus: 'Focus block',
      action: 'Action item',
      system_action: 'Workspace action',
      reminder: 'Reminder',
    };
    return map[this.task().type] ?? this.task().type;
  });
  protected readonly sourceLabel = computed(() => {
    const s = this.task().source;
    if (s === 'planner') return 'Planner';
    if (s === 'todo') return 'To Do';
    return null;
  });
  protected readonly sourceLongLabel = computed(() => {
    const map: Record<string, string> = {
      user: 'Created by you',
      ai: 'AI planner',
      system: 'Workspace',
      admin: 'Workspace',
      planner: 'Microsoft Planner',
      todo: 'Microsoft To Do',
      external: 'External app',
    };
    return map[this.task().source] ?? this.task().source;
  });

  private timeOf(value?: string): string | null {
    if (!value) return null;
    // scheduledStartTime is an ISO instant OR a bare HH:mm depending on age.
    if (!value.includes('T')) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString([], withClock({ hour: '2-digit', minute: '2-digit', hour12: false }));
  }

  protected readonly whenLabel = computed(() => {
    const t = this.task();
    if (!t.scheduledDate) return 'Unscheduled';
    const day = dayLabelOf(t.scheduledDate);
    const start = this.timeOf(t.scheduledStartTime);
    const end = this.timeOf(t.scheduledEndTime);
    if (start && end) return `${day} · ${start}–${end}`;
    if (start) return `${day} · ${start}`;
    return `${day} · any time`;
  });
  protected readonly subLine = computed(() => `· ${this.whenLabel()}`);
  protected readonly durationLabel = computed(() => {
    const mins = this.task().estimatedDuration;
    if (!mins) return '—';
    if (mins < 60) return `${mins}m`;
    return mins % 60 === 0 ? `${mins / 60}h` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
  });
  protected readonly dueLabel = computed(() => {
    const due = this.task().dueDate;
    return due ? dayLabelOf(due.slice(0, 10)) : '—';
  });
  protected readonly completedLabel = computed(() => {
    const at = this.task().completedAt;
    if (!at) return '—';
    const d = new Date(at);
    return Number.isNaN(d.getTime()) ? '—' : `${dayLabelOf(localDateStr(d))} · ${d.toLocaleTimeString([], withClock({ hour: '2-digit', minute: '2-digit', hour12: false }))}`;
  });

  // ── Actions ──

  private toast(msg: string): void {
    this.toolToast.set(msg);
    if (this._toastTimer) clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => this.toolToast.set(null), 2200);
  }

  private captureAction(action: 'complete' | 'defer' | 'edit' | 'delete' | 'open-source'): void {
    this.analytics.capture(ANALYTICS_EVENTS.TASK_DETAIL_ACTION, {
      task_id: this.task()._id,
      action,
      task_source: this.capabilitySource(),
    });
  }

  protected async onComplete(): Promise<void> {
    this.captureAction('complete');
    this.busy.set(true);
    await this.tasks.completeTask(this.task()._id);
    this.busy.set(false);
    this.toast('Task marked done');
    this.changed.emit({ ...this.task(), status: 'done', completedAt: new Date().toISOString() });
  }

  protected async onDefer(): Promise<void> {
    this.captureAction('defer');
    const base = this.task().scheduledDate
      ? new Date(this.task().scheduledDate + 'T12:00:00')
      : new Date();
    base.setDate(base.getDate() + 1);
    const newDate = localDateStr(base);
    this.busy.set(true);
    await this.tasks.deferTask(this.task()._id, newDate);
    this.busy.set(false);
    this.toast(`Deferred to ${dayLabelOf(newDate)}`);
    this.changed.emit({
      ...this.task(),
      scheduledDate: newDate,
      scheduledStartTime: undefined,
      scheduledEndTime: undefined,
      status: 'planned',
    });
  }

  protected async onDelete(): Promise<void> {
    const ok = await this.confirmDialog.confirm({
      title: 'Delete this task?',
      message: 'This removes the task from your plan. This can\u2019t be undone.',
      confirmLabel: 'Delete task',
      cancelLabel: 'Keep it',
      danger: true,
    });
    if (!ok) return;
    this.captureAction('delete');
    this.busy.set(true);
    const deleted = await this.tasks.deleteTask(this.task()._id);
    this.busy.set(false);
    if (deleted) this.close.emit();
    else this.toast('Could not delete — try again');
  }

  protected onEdit(): void {
    this.captureAction('edit');
    this.edit.emit(this.task());
  }

  protected onOpenSource(url: string): void {
    const safeUrl = safeTaskSourceUrl(url);
    if (!safeUrl) return;
    this.captureAction('open-source');
    window.open(safeUrl, '_blank', 'noopener,noreferrer');
  }
}
